import { Log, useProxyWatch, useTranslation } from '@fanfanlo';
import {
    Search as SearchIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { matrixInivte } from '../../../client/invite';
import { roomData } from '../../../client/room/data';
import { roomSettingsService } from '../../../client/room/roomSettingsService';
import type { IRoomMember, IRoomSettings } from '../../../client/room/types';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

const fileLogger = new Log(false, 'im.components.room-settings.member.MemberManagementContent');
// fileLogger.pause = true;
// fileLogger.childrenPaused = true;
interface IConfirmDialogProps {
    open: boolean;
    title: string;
    content: string;
    onConfirm: () => void;
    onCancel: () => void;
}

interface IMemberManagementProps {
    roomId: string;
    settings: IRoomSettings | null;
    onChange: () => void;
}

const ConfirmDialog = ({
    open,
    title,
    content,
    onConfirm,
    onCancel
}: IConfirmDialogProps) => {
    const { t } = useTranslation('homepage/im/components/room-settings/member/content');
    return (
        <Dialog open={open} onClose={onCancel}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>{content}</DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>{t('MemberManagementContent.confirmDialog.buttons.cancel')}</Button>
                <Button onClick={onConfirm} color="error">
                    {t('MemberManagementContent.confirmDialog.buttons.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface MemberCardProps {
    member: IRoomMember;
    roomId: string;
    onClick: () => void;
}

const MemberCard = ({ member, roomId, onClick }: MemberCardProps) => {
    const logger = fileLogger.sub(false, 'MemberCard_ui');
    // logger.pause = true;
    logger.log('roomId=', roomId);
    logger.log('member=', member);
    logger.log('member.userId=', member.userId);
    const { t } = useTranslation('homepage/im/components/room-settings/member/content');
    const [userLevel] = useProxyWatch(
        roomData.powerLevels,
        [roomId, 'users', member.userId],
        roomData.powerLevels[roomId]?.users?.[member.userId] ?? roomData.powerLevels[roomId]?.users_default ?? 0
    );
    logger.log(`roomData.powerLevels[roomId]?.users?.[member.userId]=`, roomData.powerLevels[roomId]?.users?.[member.userId])
    logger.log(`roomData.powerLevels[roomId]=`, roomData.powerLevels[roomId])
    logger.log('userLevel=', userLevel, roomId, member.userId);
    const [usersDefault] = useProxyWatch(
        roomData.powerLevels,
        [roomId, 'users_default'],
        roomData.powerLevels[roomId]?.users_default ?? 0
    );
    logger.log('usersDefault=', usersDefault);
    const level = userLevel ?? usersDefault ?? 0;
    let roleLabel = t('MemberManagementContent.role.user');
    if (level >= 100) roleLabel = t('MemberManagementContent.role.owner');
    else if (level >= 50) roleLabel = t('MemberManagementContent.role.admin');
    else if (level >= 20) roleLabel = t('MemberManagementContent.role.moderator');
    logger.log('roleLabel=', roleLabel);

    return (
        <Card
            key={member.userId}
            variant="outlined"
            sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer' }}
            onClick={onClick}
        >
            <Box sx={{ flex: '0 0 auto', minWidth: 48, minHeight: 48, mr: 2, display: 'flex', alignItems: 'center' }}>
                {member.avatarUrl ? (
                    <Box
                        component="img"
                        src={member.avatarUrl}
                        alt={t('MemberManagementContent.table.avatar.alt')}
                        sx={{ width: 48, height: 48, borderRadius: '50%' }}
                    />
                ) : (
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                        {member.displayName?.[0] || member.userId[0]}
                    </Box>
                )}
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}>
                <Typography variant="subtitle1" noWrap>{member.displayName || member.userId}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{member.userId}</Typography>
            </Box>
            <Box sx={{ flex: '0 0 auto', textAlign: 'right', ml: 2 }}>
                <Chip label={roleLabel} size="small" color="primary" />
            </Box>
        </Card>
    );
};

export const MemberManagement = ({
    roomId,
    settings,
    onChange
}: IMemberManagementProps) => {
    const { t } = useTranslation('homepage/im/components/room-settings/member/content');
    const [loading, setLoading] = useState(false);
    const [members] = useProxyWatch(
        roomData.roomMembers,
        [roomId],
        roomData.roomMembers[roomId] || []
    );
    const [searchText, setSearchText] = useState('');
    const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        title: string;
        content: string;
        onConfirm: () => void;
    }>({
        open: false,
        title: '',
        content: '',
        onConfirm: () => {}
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [inviteUserId, setInviteUserId] = useState('');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const router = useRouter();

    const handleKickMember = (userId: string) => {
        setConfirmDialog({
            open: true,
            title: t('MemberManagementContent.confirmDialog.kick.title'),
            content: t('MemberManagementContent.confirmDialog.kick.content'),
            onConfirm: async () => {
                try {
                    await roomSettingsService.kickMember(roomId, userId);
                    toast.success(t('MemberManagementContent.toast.kick.success'));
                    onChange();
                } catch (error: any) {
                    toast.error(error.message || t('MemberManagementContent.toast.kick.error'));
                }
                setConfirmDialog(prev => ({ ...prev, open: false }));
            }
        });
    };

    const handleBanMember = (userId: string) => {
        setConfirmDialog({
            open: true,
            title: t('MemberManagementContent.confirmDialog.ban.title'),
            content: t('MemberManagementContent.confirmDialog.ban.content'),
            onConfirm: async () => {
                try {
                    await roomSettingsService.banMember(roomId, userId);
                    toast.success(t('MemberManagementContent.toast.ban.success'));
                    onChange();
                } catch (error: any) {
                    toast.error(error.message || t('MemberManagementContent.toast.ban.error'));
                }
                setConfirmDialog(prev => ({ ...prev, open: false }));
            }
        });
    };

    const handleInvite = async () => {
        setInviteLoading(true);
        setInviteError(null);
        try {
            const result = await matrixInivte.inviteToRoom(roomId, inviteUserId.trim());
            if (result.success) {
                toast.success(t('MemberManagementContent.invite.success'));
                setInviteDialogOpen(false);
                setInviteUserId('');
                onChange();
            } else {
                setInviteError(result.error || t('MemberManagementContent.invite.error'));
            }
        } catch (e: any) {
            setInviteError(e.message || t('MemberManagementContent.invite.error'));
        } finally {
            setInviteLoading(false);
        }
    };

    const filteredMembers = members.filter(member =>
        member.displayName?.toLowerCase().includes(searchText.toLowerCase()) ||
        member.userId.toLowerCase().includes(searchText.toLowerCase())
    );
    console.log('[MemberManagement] members:', members);
    console.log('[MemberManagement] filteredMembers:', filteredMembers);
    console.log('[MemberManagement] settings:', settings);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder={t('MemberManagementContent.search.placeholder')}
                    value={searchText}
                    onChange={e => {
                        console.log('[MemberManagement] searchText change:', e.target.value);
                        setSearchText(e.target.value);
                    }}
                    sx={{ width: 300 }}
                    InputProps={{
                        startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                    }}
                />
                <Button
                    variant="contained"
                    sx={{ ml: 2 }}
                    onClick={() => {
                        console.log('[MemberManagement] open invite dialog');
                        setInviteDialogOpen(true);
                    }}
                >
                    {t('MemberManagementContent.invite.button')}
                </Button>
            </Box>

            <Stack spacing={2}>
                {filteredMembers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((member) => (
                        <MemberCard
                            key={member.userId}
                            member={member}
                            roomId={roomId}
                            onClick={() => {
                                router.push({
                                    pathname: '/im/room/room-settings/member-detail',
                                    query: { roomId, userId: member.userId }
                                });
                            }}
                        />
                    ))}
            </Stack>

            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                content={confirmDialog.content}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            />

            <Snackbar
                open={!!message}
                autoHideDuration={6000}
                onClose={() => setMessage(null)}
            >
                <Alert
                    onClose={() => setMessage(null)}
                    severity={message?.type}
                    sx={{ width: '100%' }}
                >
                    {message?.text}
                </Alert>
            </Snackbar>

            <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)}>
                <DialogTitle>{t('MemberManagementContent.invite.title')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('MemberManagementContent.invite.inputLabel')}
                        fullWidth
                        value={inviteUserId}
                        onChange={e => setInviteUserId(e.target.value)}
                        disabled={inviteLoading}
                    />
                    {inviteError && <Alert severity="error" sx={{ mt: 2 }}>{inviteError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInviteDialogOpen(false)} disabled={inviteLoading}>
                        {t('MemberManagementContent.invite.cancel')}
                    </Button>
                    <Button onClick={handleInvite} disabled={inviteLoading || !inviteUserId.trim()} variant="contained">
                        {inviteLoading ? t('MemberManagementContent.invite.loading') : t('MemberManagementContent.invite.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export const MemberManagementContent = ({ roomId }: { roomId: string }) => {
    const { t } = useTranslation('homepage/im/components/room-settings/member/content');
    const [loading, setLoading] = useState(false);
    const [currentSettings] = useProxyWatch(
        roomData.roomSettings.settings,
        [roomId],
        roomData.roomSettings.settings[roomId]
    );
    const handleSettingsChange = () => {};
    return (
        <MemberManagement
            roomId={roomId}
            settings={currentSettings}
            onChange={handleSettingsChange}
        />
    );
};